import { Access } from "@p0security/shared/types/access/index";

import {
  MIRO_AI_BOARD_ROLES,
  MIRO_AI_PRIOR_ROLES,
  MIRO_AI_ROLES_SET,
} from "./roles.ts";

/** The agentic catalog key of the Miro (board access) MCP server, the one
 * server a miro-ai grant writes entitlements on. It is stored as each added
 * server's `definition.id`, so it never changes. */
export const MIRO_BOARD_SERVER = "miroBoard";

/** The `DONE` message (spec section 5). Its parameter is typed loosely here
 * rather than inferred from the access, which inference can't do from inside
 * the access's own definition: a callback that reads the inferred permission
 * collapses the access's types. */
const doneMessage = ({
  permission,
}: {
  permission: Readonly<Record<string, unknown>>;
}) => {
  const { boardName, role, team } = permission;
  return `You have ${String(role)} on the Miro board ${String(boardName)} in ${String(team)}. Your own Miro user now has this role on the board. Sign in to the Miro (board access) server as yourself, or your agent won't see the board.`;
};

// The access adds the requester's own Miro user, found by their P0 email in
// one installed team, to one board at one role, and gives the agent session
// that asked one gateway entitlement per granted tool. An agent's request
// reads `board <gateway> <server> <team> <board> <role>`: the gateway and the
// `miroBoard` server come first, from the server's own prefix arguments, as
// Jira (AI)'s do, because each entitlement row names the server.
//
// Normalize resolves the team's and the member's Miro ids, and the board's
// name, and sets them as the generated parameters below, so policies and the
// overlap checks read them from the permission. `generated` holds only what
// stage and revoke record: the member's prior role, the role P0 set, and how
// the grant ended.
export const MiroAiAccesses = {
  board: Access({
    delegation: {},
    description:
      "A role on one Miro board, for your own Miro user and the agent signed in as you",
    generated: {
      // The member's role when the request was staged, which a revoke
      // restores when no other request still holds a role. Required: a
      // request without it can't be revoked safely.
      priorRole: {
        choices: MIRO_AI_PRIOR_ROLES,
        description: "The board role restored when this access ends",
        label: "Prior board role",
        type: "string",
      },
      // `unchanged` when the member already held the role or one above it,
      // so P0 wrote nothing and its revoke changes nothing.
      roleSet: {
        choices: MIRO_AI_ROLES_SET,
        description:
          "The board role P0 set, or unchanged when your Miro user already had it",
        label: "Board role P0 set",
        type: "string",
      },
      // The grant's end, as each gateway entitlement row's `until`.
      until: { label: "Registered expiration time", type: "string" },
      // Set by the revoke when the member's role was one P0 couldn't have
      // left there: someone changed it in Miro during the grant, and P0 left
      // it as it found it.
      changedOutsideP0: {
        description:
          "Someone changed your board role in Miro during this grant, so P0 left it as it found it",
        label: "Changed outside P0",
        optional: true,
        properties: { role: { label: "Role found", type: "string" } },
        type: "object",
      },
      // Set by the revoke when Miro refused it because the connector user
      // lost the board's owner or co-owner role.
      revokeFailed: {
        description:
          "P0 couldn't take this access back, because its Miro connector user lost the role it needs on the board",
        label: "Revoke failed",
        optional: true,
        properties: {
          board: { label: "Board", type: "string" },
          missingRole: { label: "Missing role", type: "string" },
        },
        type: "object",
      },
    },
    label: "Board",
    maturity: "beta",
    parameters: {
      gateway: {
        description: "Gateway that hosts agentic access",
        label: "Gateway",
        type: "input",
      },
      server: {
        description: "Gateway-hosted MCP server",
        label: "MCP server",
        type: "input",
      },
      // A `team` item's id.
      team: {
        description: "Miro team, by its P0 identifier",
        label: "Team",
        type: "static",
      },
      board: {
        description: "Miro board ID",
        hint: "The ID in the board's URL, e.g. /app/board/<id>/",
        label: "Board",
        type: "static",
      },
      role: {
        choices: MIRO_AI_BOARD_ROLES,
        description: "Board role to grant",
        label: "Role",
        type: "static",
      },
      teamId: {
        description: "The Miro team's own ID",
        label: "Miro team ID",
        type: "generated",
      },
      memberId: {
        description: "Your Miro user, found by your email in the team",
        label: "Miro user ID",
        type: "generated",
      },
      // Read from the connector when the request is made, so a rule can
      // match it, as in `^\[Public\]`.
      boardName: {
        description: "The board's name in Miro when you requested it",
        label: "Board name",
        type: "generated",
      },
    },
    render: {
      instructions: doneMessage,
      locator: "team",
      policy: "role",
      resource: "board",
    } as const,
    // Miro gives a member one role per board.
    singleValued: { parameter: "role", per: "board" },
  }),
};

export type MiroAiAccesses = typeof MiroAiAccesses;
export type MiroAiBoardAccess = MiroAiAccesses["board"];
